import { DataGuard } from "@components/DataGuard"
import { Match, Show, Switch, createEffect, createSignal, untrack } from "solid-js"
import { useService } from "solid-services"
import { Grid } from "styled-system/jsx"

import { useLessonState } from "../state"
import { useQuizState } from "./useQuizState"

import QuizQuestionCard from "./Card"
import QuizIntro from "./Intro"
import QuizNavigation from "./Navigation"
import QuizProgress from "./Progress"
import QuizResultBanner from "./ResultBanner"

type ViewState = 'intro' | 'active' | 'review'

export default function QuizContent() {
  const { lessonQuery, setRequirementsMet } = useService(useLessonState)()
  const { quizQuery, submitQuizMutation } = useQuizState()

  const passingThreshold = () => (lessonQuery.data?.passing_threshold ?? 100) / 100

  const [viewState, setViewState] = createSignal<ViewState>('intro')
  const [currentQuestionIndex, setCurrentQuestionIndex] = createSignal(0)
  const [answers, setAnswers] = createSignal<Record<string, number[]>>({})

  // New state to hold their historical score so the Intro component can display it
  const [historicalScore, setHistoricalScore] = createSignal<number | null>(null)
  const [isRestored, setIsRestored] = createSignal(false)

  // 1. Just process the DB data, but DO NOT auto-change the viewState!
  createEffect(() => {
    const data = quizQuery.data

    if (quizQuery.isSuccess && data && !untrack(isRestored)) {
      const { questions, submissions } = data
      setIsRestored(true)

      if (submissions.length > 0 && submissions.length === questions.length) {
        const correctCount = submissions.filter((sub: any) => sub.is_correct).length
        const score = correctCount / questions.length

        setHistoricalScore(score)

        // Restore their answers secretly in the background so Review Mode works
        const restoredAnswers: Record<string, number[]> = {}
        for (const sub of submissions) {
          restoredAnswers[sub.question_id] = sub.selected_indices
        }
        setAnswers(restoredAnswers)

        // Unlock next lesson if their historical attempt was a pass
        if (score >= passingThreshold()) setRequirementsMet(true)
      }
    }
  })

  const toggleSelection = (questionId: string, optionIndex: number, isMulti: boolean) => {
    setAnswers(prev => {
      const current = prev[questionId] || []
      if (!isMulti) return { ...prev, [questionId]: [optionIndex] }
      if (current.includes(optionIndex)) {
        return { ...prev, [questionId]: current.filter(i => i !== optionIndex) }
      }
      return { ...prev, [questionId]: [...current, optionIndex] }
    })
  }

  const handleNext = () => setCurrentQuestionIndex(prev => prev + 1)
  const handlePrev = () => setCurrentQuestionIndex(prev => prev - 1)

  const handleSubmit = async () => {
    const data = quizQuery.data
    if (!data) return

    let correctCount = 0

    const payload = data.questions.map((q: any) => {
      const selected = answers()[q.id] || []
      const isCorrect = JSON.stringify([...selected].sort()) === JSON.stringify([...q.correct_indices].sort())
      if (isCorrect) correctCount++

      return {
        question_id: q.id,
        selected_indices: selected,
        is_correct: isCorrect
      }
    })

    const score = correctCount / data.questions.length
    const passed = score >= passingThreshold()

    await submitQuizMutation.mutateAsync(payload)

    // Update their historical score locally to match the new submission
    setHistoricalScore(score)

    if (passed) setRequirementsMet(true)
    setViewState('review')
    setCurrentQuestionIndex(0)
  }

  const startRetake = () => {
    setAnswers({}) // Clear local answers for a fresh start
    setRequirementsMet(false)
    setCurrentQuestionIndex(0)
    setViewState('active')
  }

  const startReview = () => {
    // Answers are already hydrated from the createEffect
    setCurrentQuestionIndex(0)
    setViewState('review')
  }

  return (
    <DataGuard query={quizQuery}>
      {(data) => {
        if (!data) return null

        const { questions } = data
        const question = () => questions?.[currentQuestionIndex()]
        const totalQuestions = questions?.length || 0

        // Use our local historical score, or fallback to calculating it
        const isReviewPass = historicalScore() !== null ? historicalScore()! >= passingThreshold() : false

        return (
          <Grid maxW="3xl" mx="auto" w="full">
            <Switch>
              <Match when={viewState() === 'intro'}>
                <QuizIntro
                  content={lessonQuery.data?.content ?? undefined}
                  hasPreviousAttempt={historicalScore() !== null}
                  previousScore={historicalScore() || 0}
                  passingThreshold={passingThreshold()}
                  onStart={startRetake}
                  onReview={startReview}
                />
              </Match>

              <Match when={(viewState() === 'active' || viewState() === 'review') && question()}>
                <Show when={viewState() === 'review'}>
                  <QuizResultBanner allCorrect={isReviewPass} />
                </Show>

                <QuizQuestionCard
                  question={question()!}
                  isReview={viewState() === 'review'}
                  // Use local state for submissions during review mode so it updates properly
                  submission={{ selected_indices: answers()[question()!.id] || [], is_correct: JSON.stringify([...(answers()[question()!.id] || [])].sort()) === JSON.stringify([...question()!.correct_indices].sort()) }}
                  selectedIndices={answers()[question()!.id] || []}
                  onToggleSelection={(index, isMulti) => toggleSelection(question()!.id, index, isMulti)}
                />

                <QuizProgress currentIndex={currentQuestionIndex()} totalQuestions={totalQuestions} />

                <QuizNavigation
                  currentIndex={currentQuestionIndex()}
                  totalQuestions={totalQuestions}
                  viewState={viewState()}
                  isPending={submitQuizMutation.isPending}
                  isNextDisabled={viewState() === 'active' && (answers()[question()!.id]?.length ?? 0) === 0}
                  isPassed={isReviewPass}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onRetake={startRetake}
                  onSubmit={handleSubmit}
                />
              </Match>
            </Switch>
          </Grid>
        )
      }}
    </DataGuard>
  )
}
